import React, { useState, useEffect } from 'react';
import { Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableDropdown from '../common/SearchableDropdown';
import NumericInput from '../common/NumericInput';
import DatePicker from '../common/DatePicker';
import { diagnosticService, careService, clinicService, adminService, patientService, doctorService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { toSafeFixed } from '../../utils/numberUtils';

interface TestOrderFormProps {
  onSave: () => void;
  onCancel: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm?: boolean;
  editData?: any;
}

interface OrderItem {
  test_id: number;
  test_name: string;
  panel_id: number;
  panel_name: string;
  is_panel: boolean;
  rate: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
  cess_rate: number;
  disc_percentage: number;
  disc_amount: number;
  taxable_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  cess_amount: number;
  total_amount: number;
}

const TestOrderForm: React.FC<TestOrderFormProps> = ({ onSave, onCancel, isCollapsed, onToggleCollapse, resetForm, editData }) => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [tests, setTests] = useState<any[]>([]);
  const [panels, setPanels] = useState<any[]>([]);
  const [agencyOptions, setAgencyOptions] = useState<{value: string, label: string}[]>([]);
  const [patientOptions, setPatientOptions] = useState<{value: string, label: string}[]>([]);
  const [doctorOptions, setDoctorOptions] = useState<{value: string, label: string}[]>([]);
  
  const generateOrderNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const fff = String(now.getMilliseconds()).padStart(3, '0');
    return `TO-${tenantId}${dd}${mm}${yyyy}${hh}${min}${ss}${fff}`;
  };

  const [formData, setFormData] = useState({
    test_order_number: generateOrderNumber(),
    appointment_id: '',
    patient_id: '',
    patient_name: '',
    patient_phone: '',
    doctor_id: '',
    doctor_name: '',
    doctor_phone: '',
    doctor_license_number: '',
    order_date: new Date().toISOString().split('T')[0],
    status: 'ORDERED',
    urgency: 'ROUTINE',
    notes: '',
    agency_id: '',
    disc_percentage: 0,
    roundoff: 0
  });

  const [isPatientReadonly, setIsPatientReadonly] = useState(false);
  const [isDoctorReadonly, setIsDoctorReadonly] = useState(false);

  const [items, setItems] = useState<OrderItem[]>([{
    test_id: 0,
    test_name: '',
    panel_id: 0,
    panel_name: '',
    is_panel: false,
    rate: 0,
    cgst_rate: 0,
    sgst_rate: 0,
    igst_rate: 0,
    cess_rate: 0,
    disc_percentage: 0,
    disc_amount: 0,
    taxable_amount: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    igst_amount: 0,
    cess_amount: 0,
    total_amount: 0
  }]);

  const { showToast } = useToast();

  useEffect(() => {
    loadAppointments('');
    loadPatients('');
    loadDoctors('');
    loadTests();
    loadPanels();
    loadAgencies('').then(options => setAgencyOptions(options));
  }, []);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        test_order_number: generateOrderNumber(),
        appointment_id: '',
        patient_id: '',
        patient_name: '',
        patient_phone: '',
        doctor_id: '',
        doctor_name: '',
        doctor_phone: '',
        doctor_license_number: '',
        order_date: new Date().toISOString().split('T')[0],
        status: 'ORDERED',
        urgency: 'ROUTINE',
        notes: '',
        agency_id: '',
        disc_percentage: 0,
        roundoff: 0
      });
      setIsPatientReadonly(false);
      setIsDoctorReadonly(false);
      setItems([{
        test_id: 0,
        test_name: '',
        panel_id: 0,
        panel_name: '',
        is_panel: false,
        rate: 0,
        cgst_rate: 0,
        sgst_rate: 0,
        igst_rate: 0,
        cess_rate: 0,
        disc_percentage: 0,
        disc_amount: 0,
        taxable_amount: 0,
        cgst_amount: 0,
        sgst_amount: 0,
        igst_amount: 0,
        cess_amount: 0,
        total_amount: 0
      }]);
    }
  }, [resetForm]);

  useEffect(() => {
    if (editData) {
      setFormData({
        test_order_number: editData.test_order_number || '',
        appointment_id: editData.appointment_id?.toString() || '',
        patient_id: editData.patient_id?.toString() || '',
        patient_name: editData.patient_name || '',
        patient_phone: editData.patient_phone || '',
        doctor_id: editData.doctor_id?.toString() || '',
        doctor_name: editData.doctor_name || '',
        doctor_phone: editData.doctor_phone || '',
        doctor_license_number: editData.doctor_license_number || '',
        order_date: editData.order_date ? new Date(editData.order_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        status: editData.status || 'pending',
        urgency: editData.urgency || 'normal',
        notes: editData.notes || '',
        agency_id: editData.agency_id?.toString() || '',
        disc_percentage: editData.disc_percentage || 0,
        roundoff: editData.roundoff || 0
      });
      
      // Set readonly state if came from appointment
      if (editData.appointment_id) {
        setIsPatientReadonly(true);
        setIsDoctorReadonly(true);
      }
      
      if (editData.items && editData.items.length > 0) {
        setItems(editData.items.map((item: any) => ({
          test_id: item.test_id || 0,
          test_name: item.test_name || '',
          panel_id: item.panel_id || 0,
          panel_name: item.panel_name || '',
          is_panel: !!item.panel_id,
          rate: item.rate || 0,
          cgst_rate: item.cgst_rate || 0,
          sgst_rate: item.sgst_rate || 0,
          igst_rate: item.igst_rate || 0,
          cess_rate: item.cess_rate || 0,
          disc_percentage: item.disc_percentage || 0,
          disc_amount: item.disc_amount || 0,
          taxable_amount: item.taxable_amount || 0,
          cgst_amount: item.cgst_amount || 0,
          sgst_amount: item.sgst_amount || 0,
          igst_amount: item.igst_amount || 0,
          cess_amount: item.cess_amount || 0,
          total_amount: item.total_amount || 0
        })));
      }
    }
  }, [editData]);

  const loadAppointments = async (search: string = '') => {
    try {
      const response = await clinicService.getAppointments({ search, per_page: 50 });
      setAppointments(response.data);
      return response.data.map((apt: any) => ({
        value: apt.id.toString(),
        label: apt.appointment_number || apt.id.toString()
      }));
    } catch (error) {
      return [];
    }
  };

  const loadPatients = async (search: string = '') => {
    try {
      const response = await patientService.getPatients({ search, per_page: 1000 });
      const patientList = response.data || [];
      setPatients(patientList);
      const options = patientList.map((patient: any) => {
        const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
        return {
          value: patient.id.toString(),
          label: `${patient.phone || ''}|${fullName}`.trim()
        };
      });
      setPatientOptions(options);
      return options;
    } catch (error) {
      showToast('error', 'Failed to load patients');
      return [];
    }
  };

  const loadDoctors = async (search: string = '') => {
    try {
      const response = await doctorService.getDoctors({ search, per_page: 1000 });
      const doctorList = response.data || [];
      setDoctors(doctorList);
      const options = doctorList.map((doctor: any) => {
        const fullName = `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim();
        return {
          value: doctor.id.toString(),
          label: `${doctor.phone || ''}|${fullName}`.trim()
        };
      });
      setDoctorOptions(options);
      return options;
    } catch (error) {
      showToast('error', 'Failed to load doctors');
      return [];
    }
  };

  const loadTests = async () => {
    try {
      const response = await careService.getTests({ per_page: 1000 });
      setTests(response.data);
    } catch (error) {
      showToast('error', 'Failed to load tests');
    }
  };

  const loadPanels = async () => {
    try {
      const response = await diagnosticService.getTestPanels({ per_page: 1000 });
      setPanels(response.data);
    } catch (error) {
      showToast('error', 'Failed to load panels');
    }
  };

  const loadAgencies = async (search: string) => {
    try {
      const response = await adminService.getAgencies({ search, per_page: 50 });
      return response.data.map((agency: any) => ({
        value: agency.id.toString(),
        label: `${agency.phone} | ${agency.name}`
      }));
    } catch (error) {
      return [];
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'disc_percentage') {
      setFormData(prev => ({ ...prev, disc_percentage: parseFloat(value) || 0 }));
    } else if (name === 'disc_amount') {
      const discAmount = parseFloat(value) || 0;
      const { subtotal: currentSubtotal } = calculateTotals();
      const discPercentage = currentSubtotal > 0 ? (discAmount / currentSubtotal) * 100 : 0;
      setFormData(prev => ({ ...prev, disc_percentage: discPercentage }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'roundoff' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handleAppointmentChange = (value: string | number | (string | number)[]) => {
    const appointmentId = value.toString();
    const appointment = appointments.find(a => a.id === Number(appointmentId));
    setFormData(prev => ({
      ...prev,
      appointment_id: appointmentId,
      patient_id: appointment?.patient_id?.toString() || '',
      patient_name: appointment?.patient_name || '',
      patient_phone: appointment?.patient_phone || '',
      doctor_id: appointment?.doctor_id?.toString() || '',
      doctor_name: appointment?.doctor_name || '',
      doctor_phone: appointment?.doctor_phone || '',
      doctor_license_number: appointment?.doctor_license_number || ''
    }));
    setIsPatientReadonly(true);
    setIsDoctorReadonly(true);
  };

  const handlePatientChange = (value: string | number | (string | number)[]) => {
    const patientId = value.toString();
    let patient = patients.find(p => p.id === Number(patientId));
    
    // If patient not found in array, try to find in formData or use option data
    if (!patient) {
      // Patient might just have been created and not in patients array yet
      // We'll extract info from the formData if it exists there
      if (formData.patient_id === patientId) {
        // Patient data is already in form, no need to update
        return;
      }
    }
    
    const fullName = patient ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() : '';
    setFormData(prev => ({
      ...prev,
      patient_id: patientId,
      patient_name: fullName || prev.patient_name, // Keep previous name if not found
      patient_phone: patient?.phone || prev.patient_phone
    }));
  };

  const handleDoctorChange = (value: string | number | (string | number)[]) => {
    const doctorId = value.toString();
    let doctor = doctors.find(d => d.id === Number(doctorId));
    
    // If doctor not found in array, try to find in formData or use option data
    if (!doctor) {
      // Doctor might just have been created and not in doctors array yet
      // We'll extract info from the formData if it exists there
      if (formData.doctor_id === doctorId) {
        // Doctor data is already in form, no need to update
        return;
      }
    }
    
    const fullName = doctor ? `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() : '';
    setFormData(prev => ({
      ...prev,
      doctor_id: doctorId,
      doctor_name: fullName || prev.doctor_name, // Keep previous name if not found
      doctor_phone: doctor?.phone || prev.doctor_phone,
      doctor_license_number: doctor?.license_number || prev.doctor_license_number
    }));
  };

  const handleTestChange = (index: number, value: string | number | (string | number)[]) => {
    const testId = Array.isArray(value) ? value[0] : value;
    const test = tests.find(t => t.id === Number(testId));
    
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      test_id: Number(testId),
      test_name: test?.name || '',
      panel_id: 0,
      panel_name: '',
      rate: test?.rate || 0,
      disc_percentage:0,
      cgst_rate: (test?.gst || 0) / 2,
      sgst_rate: (test?.gst || 0) / 2,
      igst_rate: 0,
      cess_rate: test?.cess || 0
    };
    
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const handlePanelChange = (index: number, value: string | number | (string | number)[]) => {
    const panelId = Array.isArray(value) ? value[0] : value;
    const panel = panels.find(p => p.id === Number(panelId));
    
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      test_id: 0,
      test_name: '',
      panel_id: Number(panelId),
      panel_name: panel?.name || '',
      rate: panel?.cost || 0,
      cgst_rate: (panel?.gst || 0) / 2,
      sgst_rate: (panel?.gst || 0) / 2,
      igst_rate: 0,
      cess_rate: panel?.cess || 0
    };
    
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const calculateItemTotal = (index: number, itemsArray: OrderItem[]) => {
    const item = itemsArray[index];
    const baseAmount = item.rate;
    const discountAmount = baseAmount * (item.disc_percentage / 100);
    const taxableAmount = baseAmount - discountAmount;
    const cgstAmount = taxableAmount * (item.cgst_rate / 100);
    const sgstAmount = taxableAmount * (item.sgst_rate / 100);
    const igstAmount = taxableAmount * (item.igst_rate / 100);
    const cessAmount = taxableAmount * (item.cess_rate / 100);
    const total = taxableAmount + cgstAmount + sgstAmount + igstAmount + cessAmount;

    itemsArray[index] = {
      ...item,
      disc_amount: discountAmount,
      taxable_amount: taxableAmount,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      igst_amount: igstAmount,
      cess_amount: cessAmount,
      total_amount: total
    };
    setItems([...itemsArray]);
  };

  const addItem = () => {
    setItems([...items, {
      test_id: 0,
      test_name: '',
      panel_id: 0,
      panel_name: '',
      is_panel: false,
      rate: 0,
      cgst_rate: 0,
      sgst_rate: 0,
      igst_rate: 0,
      cess_rate: 0,
      disc_percentage: 0,
      disc_amount: 0,
      taxable_amount: 0,
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      cess_amount: 0,
      total_amount: 0
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    // Subtotal is the sum of all item total amounts (including taxes)
    const itemsTotal = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    const subtotal = itemsTotal;
    const itemsDiscountAmount = items.reduce((sum, item) => sum + (item.disc_amount || 0), 0);
    const taxableAmount = items.reduce((sum, item) => sum + (item.taxable_amount || 0), 0);
    const cgstAmount = items.reduce((sum, item) => sum + (item.cgst_amount || 0), 0);
    const sgstAmount = items.reduce((sum, item) => sum + (item.sgst_amount || 0), 0);
    const igstAmount = items.reduce((sum, item) => sum + (item.igst_amount || 0), 0);
    const cessAmount = items.reduce((sum, item) => sum + (item.cess_amount || 0), 0);
    // Overall discount applies on subtotal (sum of item totals)
    const overallDiscAmount = subtotal * (formData.disc_percentage / 100);
    const finalTotal = subtotal - overallDiscAmount; // roundoff is hidden; always 0 when sending to API
    return { subtotal, itemsDiscountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, itemsTotal, overallDiscAmount, finalTotal };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.patient_name) {
      showToast('error', 'Please select a patient');
      return;
    }

    const validItems = items.filter(item => (item.test_id > 0 || item.panel_id > 0)).map((item, idx) => ({
      line_no: idx + 1,
      test_id: item.test_id > 0 ? item.test_id : null,
      test_name: item.test_name,
      panel_id: item.panel_id > 0 ? item.panel_id : null,
      panel_name: item.panel_name,
      rate: item.rate,
      disc_percentage: item.disc_percentage,
      disc_amount: item.disc_amount,
      taxable_amount: item.taxable_amount,
      cgst_rate: item.cgst_rate,
      cgst_amount: item.cgst_amount,
      sgst_rate: item.sgst_rate,
      sgst_amount: item.sgst_amount,
      igst_rate: item.igst_rate,
      igst_amount: item.igst_amount,
      cess_rate: item.cess_rate,
      cess_amount: item.cess_amount,
      total_amount: item.total_amount,
      item_status: 'PENDING',
      remarks: ''
    }));

    if (validItems.length === 0) {
      showToast('error', 'Please add at least one test or panel');
      return;
    }

    try {
      const { subtotal, itemsDiscountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, overallDiscAmount, finalTotal } = calculateTotals();
      
      const selectedAppointment = appointments.find(apt => apt.id === Number(formData.appointment_id));
      
      const orderData = {
        test_order_number: formData.test_order_number,
        order_date: new Date(formData.order_date).toISOString(),
        patient_id: formData.patient_id ? Number(formData.patient_id) : (selectedAppointment?.patient_id || null),
        patient_name: formData.patient_name,
        patient_phone: formData.patient_phone,
        doctor_id: formData.doctor_id ? Number(formData.doctor_id) : (selectedAppointment?.doctor_id || null),
        doctor_name: formData.doctor_name || null,
        doctor_phone: formData.doctor_phone || null,
        doctor_license_number: formData.doctor_license_number || null,
        appointment_id: formData.appointment_id ? Number(formData.appointment_id) : null,
        agency_id: formData.agency_id ? Number(formData.agency_id) : null,
        subtotal_amount: subtotal,
        items_total_discount_amount: itemsDiscountAmount,
        taxable_amount: taxableAmount,
        cgst_amount: cgstAmount,
        sgst_amount: sgstAmount,
        igst_amount: igstAmount,
        cess_amount: cessAmount,
        overall_disc_percentage: formData.disc_percentage,
        overall_disc_amount: overallDiscAmount,
        overall_cess_percentage: 0,
        overall_cess_amount: 0,
        roundoff: 0,
        final_amount: finalTotal,
        urgency: formData.urgency,
        status: formData.status,
        notes: formData.notes,
        tags: null,
        items: validItems
      };

      let response;
      if (editData?.id) {
        response = await diagnosticService.updateTestOrder(editData.id, orderData);
      } else {
        response = await diagnosticService.createTestOrder(orderData);
      }
      
      if (response.success) {
        onSave();
      } else {
        showToast('error', response.message || `Failed to ${editData?.id ? 'update' : 'create'} test order`);
      }
    } catch (error) {
      showToast('error', 'Failed to create test order');
    }
  };

  const { subtotal, itemsDiscountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, itemsTotal, overallDiscAmount, finalTotal } = calculateTotals();

  return (
    <div className="bg-white rounded-lg shadow mb-4 mt-1">
      <div className="border-b border-gray-200 flex justify-between items-center" style={{ padding: 'var(--erp-section-padding)' }}>
        <h2 className="font-medium text-gray-800" style={{ fontSize: 'var(--erp-datatable-title-font-size)', lineHeight: 'var(--erp-line-height)' }}>Create Test Order</h2>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="text-gray-500 hover:text-gray-700"
        >
          {isCollapsed ? <ChevronDown className="erp-form-btn-icon" style={{ marginRight: 0 }} /> : <ChevronUp className="erp-form-btn-icon" style={{ marginRight: 0 }} />}
        </button>
      </div>

      {!isCollapsed && (
        <form onSubmit={handleSubmit} style={{ padding: 'var(--erp-card-padding)' }}>
          <div className="grid grid-cols-1 sm:grid-cols-6" style={{ gap: 'var(--erp-spacing-lg)', marginBottom: 'var(--erp-spacing-xl)' }}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Order Number</label>
              <input
                type="text"
                name="test_order_number"
                value={formData.test_order_number}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
              <DatePicker
                value={formData.order_date}
                onChange={(value) => setFormData(prev => ({ ...prev, order_date: value }))}
                placeholder="Select order date"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Appointment</label>
              <SearchableDropdown
                options={appointments.map(apt => ({
                  value: apt.id.toString(),
                  label: apt.appointment_number || apt.id.toString()
                }))}
                value={formData.appointment_id}
                onChange={handleAppointmentChange}
                placeholder="Search appointment"
                multiple={false}
                searchable={true}
                onSearch={loadAppointments}
              />
            </div>



            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Patient * <span className="text-xs text-gray-500">(Format: Phone|Name)</span></label>
              <SearchableDropdown
                options={patientOptions}
                value={formData.patient_id}
                onChange={handlePatientChange}
                onSearch={async (search) => {
                  const options = await loadPatients(search);
                  setPatientOptions(options);
                  return options;
                }}
                onAdd={async (inputValue) => {
                  const parts = inputValue.split('|').map(p => p.trim());
                  const phone = parts[0] || '';
                  const fullName = parts[1] || parts[0];
                  const nameParts = fullName.split(' ');
                  const first_name = nameParts[0] || '';
                  const last_name = nameParts.slice(1).join(' ') || '';
                  try {
                    const response = await patientService.createPatient({ first_name, last_name, phone });
                    const newPatient = response.data || response;
                    const displayName = `${newPatient.first_name || first_name} ${newPatient.last_name || last_name}`.trim();
                    const newOption = { 
                      value: newPatient.id.toString(), 
                      label: `${newPatient.phone || phone}|${displayName}` 
                    };
                    setPatients(prev => [...prev, newPatient]);
                    setPatientOptions(prev => [...prev, newOption]);
                    // Set form data for newly created patient
                    setFormData(prev => ({
                      ...prev,
                      patient_id: newPatient.id.toString(),
                      patient_name: displayName,
                      patient_phone: newPatient.phone || phone
                    }));
                    return newOption;
                  } catch (error) {
                    showToast('error', 'Failed to create patient');
                    throw error;
                  }
                }}
                placeholder="Search or add patient"
                multiple={false}
                searchable={true}
                allowAdd={true}
                disabled={isPatientReadonly}
              />
            </div>

            <div className="hidden">
              <label className="block text-xs font-medium text-gray-700 mb-1">Patient Phone</label>
              <input
                type="text"
                name="patient_phone"
                value={formData.patient_phone}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Doctor <span className="text-xs text-gray-500">(Format: Phone|Name)</span></label>
              <SearchableDropdown
                options={doctorOptions}
                value={formData.doctor_id}
                onChange={handleDoctorChange}
                onSearch={async (search) => {
                  const options = await loadDoctors(search);
                  setDoctorOptions(options);
                  return options;
                }}
                onAdd={async (inputValue) => {
                  const parts = inputValue.split('|').map(p => p.trim());
                  const phone = parts[0] || '';
                  const fullName = parts[1] || parts[0];
                  const nameParts = fullName.split(' ');
                  const first_name = nameParts[0] || '';
                  const last_name = nameParts.slice(1).join(' ') || '';
                  try {
                    const response = await doctorService.createDoctor({ first_name, last_name, phone });
                    const newDoctor = response.data || response;
                    const displayName = `${newDoctor.first_name || first_name} ${newDoctor.last_name || last_name}`.trim();
                    const newOption = { 
                      value: newDoctor.id.toString(), 
                      label: `${newDoctor.phone || phone}|${displayName}` 
                    };
                    setDoctors(prev => [...prev, newDoctor]);
                    setDoctorOptions(prev => [...prev, newOption]);
                    // Set form data for newly created doctor
                    setFormData(prev => ({
                      ...prev,
                      doctor_id: newDoctor.id.toString(),
                      doctor_name: displayName,
                      doctor_phone: newDoctor.phone || phone,
                      doctor_license_number: newDoctor.license_number || ''
                    }));
                    return newOption;
                  } catch (error) {
                    showToast('error', 'Failed to create doctor');
                    throw error;
                  }
                }}
                placeholder="Search or add doctor"
                multiple={false}
                searchable={true}
                allowAdd={true}
                disabled={isDoctorReadonly}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agency <span className="text-xs text-gray-500">(Format: Phone | Name)</span></label>
              <SearchableDropdown
                options={agencyOptions}
                value={formData.agency_id}
                onChange={(value) => setFormData(prev => ({ ...prev, agency_id: value.toString() }))}
                onSearch={async (search) => {
                  const options = await loadAgencies(search);
                  setAgencyOptions(options);
                  return options;
                }}
                onAdd={async (inputValue) => {
                  const parts = inputValue.split('|').map(p => p.trim());
                  const phone = parts[0] || '';
                  const name = parts[1] || parts[0];
                  const response = await adminService.createAgency({ name, phone });
                  const newOption = { value: response.data.id.toString(), label: `${phone} | ${name}` };
                  setAgencyOptions(prev => [...prev, newOption]);
                  return newOption;
                }}
                placeholder="Search agency"
                multiple={false}
                searchable={true}
                allowAdd={true}
              />
            </div>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-6">


            <div className="hidden">
              <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Phone</label>
              <input
                type="text"
                name="doctor_phone"
                value={formData.doctor_phone}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>

            <div className="hidden">
              <label className="block text-xs font-medium text-gray-700 mb-1">Doctor License</label>
              <input
                type="text"
                name="doctor_license_number"
                value={formData.doctor_license_number}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <SearchableDropdown
                options={[
                  { value: 'DRAFT', label: 'DRAFT' },
                  { value: 'ORDERED', label: 'ORDERED' },
                  { value: 'CANCELLED', label: 'CANCELLED' }
                ]}
                value={formData.status}
                onChange={(value) => setFormData(prev => ({ ...prev, status: value.toString() }))}
                placeholder="Select status"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Urgency</label>
              <SearchableDropdown
                options={[
                  { value: 'ROUTINE', label: 'ROUTINE' },
                  { value: 'URGENT', label: 'URGENT' },
                  { value: 'STAT', label: 'STAT' },
                  { value: 'CRITICAL', label: 'CRITICAL' }
                ]}
                value={formData.urgency}
                onChange={(value) => setFormData(prev => ({ ...prev, urgency: value.toString() }))}
                placeholder="Select urgency"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <h3 className="text-md font-semibold">Order Items</h3>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Item
            </button>
          </div>

          <div className="mb-6">
            <style>{`
              .test-order-table { table-layout: fixed !important; }
              .test-order-table th:nth-child(1), .test-order-table td:nth-child(1) { width: 5% !important; }
              .test-order-table th:nth-child(2), .test-order-table td:nth-child(2) { width: 15% !important; }
              .test-order-table th:nth-child(3), .test-order-table td:nth-child(3) { width: 7% !important; }
              .test-order-table th:nth-child(4), .test-order-table td:nth-child(4) { width: 6% !important; }
              .test-order-table th:nth-child(5), .test-order-table td:nth-child(5) { width: 6% !important; }
              .test-order-table th:nth-child(6), .test-order-table td:nth-child(6) { width: 6% !important; }
              .test-order-table th:nth-child(7), .test-order-table td:nth-child(7) { width: 7% !important; }
              .test-order-table th:nth-child(8), .test-order-table td:nth-child(8) { width: 8% !important; }
              .test-order-table th:nth-child(9), .test-order-table td:nth-child(9) { width: 8% !important; }
              .test-order-table th:nth-child(10), .test-order-table td:nth-child(10) { width: 8% !important; }
              .test-order-table th:nth-child(11), .test-order-table td:nth-child(11) { width: 8% !important; }
              .test-order-table th:nth-child(12), .test-order-table td:nth-child(12) { width: 8% !important; }
              .test-order-table th:nth-child(13), .test-order-table td:nth-child(13) { width: 4% !important; }
            `}</style>
            <div className="overflow-x-auto">
              <table className="border border-gray-200 test-order-table" style={{ minWidth: '1200px', width: '100%' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Panel</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-left">Test/Panel</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Rate</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Disc%</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Disc Amt</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Taxable</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">GST%</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">GST Amt</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">CESS%</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">CESS Amt</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Total</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-2 py-2 text-center" style={{ minWidth: '80px' }}>
                        <input
                          type="checkbox"
                          checked={item.is_panel}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index] = { ...newItems[index], is_panel: e.target.checked, test_id: 0, test_name: '', panel_id: 0, panel_name: '' };
                            setItems(newItems);
                          }}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-2 py-2" style={{ minWidth: '180px' }}>
                        {item.is_panel ? (
                          <SearchableDropdown
                            options={panels.map(panel => ({
                              value: panel.id.toString(),
                              label: panel.name
                            }))}
                            value={item.panel_id.toString()}
                            onChange={(value) => handlePanelChange(index, value)}
                            placeholder="Select panel..."
                            multiple={false}
                            searchable={true}
                            className="w-full"
                          />
                        ) : (
                          <SearchableDropdown
                            options={tests.map(test => ({
                              value: test.id.toString(),
                              label: test.name
                            }))}
                            value={item.test_id.toString()}
                            onChange={(value) => handleTestChange(index, value)}
                            placeholder="Select test..."
                            multiple={false}
                            searchable={true}
                            className="w-full"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 w-20">
                        <NumericInput
                          value={item.rate}
                          onChange={(v) => handleItemChange(index, 'rate', v)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step={0.01}
                          decimalPlaces={2}
                          name={`rate-${index}`}
                        />
                      </td>
                      <td className="px-3 py-2 w-16">
                        <NumericInput
                          value={item.disc_percentage}
                          onChange={(v) => handleItemChange(index, 'disc_percentage', v)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step={0.1}
                          decimalPlaces={2}
                          name={`disc_percentage-${index}`}
                        />
                      </td>
                      <td className="px-3 py-2 w-16">
                        <NumericInput
                          value={item.disc_amount || 0}
                          onChange={(v) => {
                            const discAmount = v || 0;
                            const discPercentage = item.rate > 0 ? (discAmount / item.rate) * 100 : 0;
                            handleItemChange(index, 'disc_percentage', discPercentage);
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step={0.01}
                          decimalPlaces={2}
                          name={`disc_amount-${index}`}
                        />
                      </td>
                      <td className="px-3 py-2 w-20 text-sm text-center">{toSafeFixed(item.taxable_amount, 2) || '0.00'}</td>
                      <td className="px-3 py-2 w-16">
                        <NumericInput
                          value={(item.cgst_rate || 0) + (item.sgst_rate || 0)}
                          onChange={(v) => {
                            const totalGst = v || 0;
                            handleItemChange(index, 'cgst_rate', totalGst / 2);
                            handleItemChange(index, 'sgst_rate', totalGst / 2);
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step={0.1}
                          decimalPlaces={2}
                          name={`gst-${index}`}
                        />
                      </td>
                      <td className="px-3 py-2 w-20 text-sm text-center">{toSafeFixed((item.cgst_amount + item.sgst_amount + item.igst_amount), 2) || '0.00'}</td>
                      <td className="px-3 py-2 w-16">
                        <NumericInput
                          value={item.cess_rate}
                          onChange={(v) => handleItemChange(index, 'cess_rate', v)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step={0.1}
                          decimalPlaces={2}
                          name={`cess-${index}`}
                        />
                      </td>
                      <td className="px-3 py-2 w-20 text-sm text-center">{toSafeFixed(item.cess_amount, 2) || '0.00'}</td>
                      <td className="px-3 py-2 w-24 text-sm text-center">{toSafeFixed(item.total_amount, 2)}</td>
                      <td className="px-3 py-2 w-16 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800"
                          disabled={items.length === 1}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end">
            <div className="w-full md:w-1/2 erp-summary-container">
              <h4 className="font-semibold" style={{ marginBottom: 'var(--erp-spacing-lg)' }}>Order Summary</h4>
              <div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Subtotal:</span>
                  <span className="erp-summary-value">{toSafeFixed((subtotal||0), 2)}</span>
                </div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Discount %:</span>
                  <NumericInput
                    value={formData.disc_percentage}
                    onChange={(v) => setFormData(prev => ({ ...prev, disc_percentage: v }))}
                    className="erp-summary-input"
                    step={0.1}
                    decimalPlaces={2}
                    name="disc_percentage"
                  />
                </div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Discount Amount:</span>
                  <NumericInput
                    value={overallDiscAmount || 0}
                    onChange={(v) => {
                      const discAmount = v || 0;
                      const discPercentage = subtotal > 0 ? (discAmount / subtotal) * 100 : 0;
                      setFormData(prev => ({ ...prev, disc_percentage: discPercentage }));
                    }}
                    className="erp-summary-input"
                    step={0.01}
                    decimalPlaces={2}
                    name="disc_amount"
                  />
                </div>
                <div className="erp-summary-row erp-summary-total">
                  <span className="erp-summary-label">Total:</span>
                  <span className="erp-summary-value">{toSafeFixed(finalTotal, 2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center" style={{ gap: 'var(--erp-spacing-lg)', marginTop: 'var(--erp-spacing-xl)' }}>
            <button
              type="button"
              onClick={onCancel}
              className="erp-form-btn text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="erp-form-btn text-white bg-primary hover:bg-secondary"
            >
              {editData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default TestOrderForm;
